import type { TFunction } from "i18next";
import { CheckCircle, Clock, XCircle } from "lucide-react";

import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { isCreditNote } from "@fiscalo/shared-types";
import type { RecibidaItem } from "@/services/invoiceService";

export function formatRecibidaTotal(currency: string, amount: number): string {
  return `${currency} ${Number(amount).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function recibidasStatusVariant(estado: string): StatusBadgeVariant {
  switch (estado) {
    case "AE":
      return "success";
    case "BE":
      return "danger";
    default:
      return "info";
  }
}

export function recibidasStatusIcon(
  estado: string
): typeof CheckCircle | typeof Clock | typeof XCircle {
  switch (estado) {
    case "AE":
      return CheckCircle;
    case "BE":
      return XCircle;
    default:
      return Clock;
  }
}

export function formatRecibidaTypeSeries(row: RecibidaItem, t: TFunction): string {
  const typeLabel = row.tipoCFE
    ? t(`invoices.cfeTypes.${row.tipoCFE}`, row.tipoCFE)
    : "";
  const serie = row.serie?.trim() ?? "";
  if (!typeLabel && !serie) return "—";
  if (typeLabel && serie) {
    return `${typeLabel} (${t("recibidas.serieLabel", "Serie")} ${serie})`;
  }
  return typeLabel || serie;
}

export function getRecibidaTotalAmount(row: RecibidaItem): number {
  const total =
    row.tipoCFE === "182" &&
    Number(row.montoTotal) === 0 &&
    Number(row.totalNeto) > 0
      ? Number(row.totalNeto)
      : Number(row.montoTotal);
  return isCreditNote(row.tipoCFE) ? -total : total;
}

export function getRecibidaNetAmount(row: RecibidaItem): number {
  return isCreditNote(row.tipoCFE)
    ? -(row.totalNeto ?? 0)
    : (row.totalNeto ?? 0);
}

export function getRecibidaIvaAmount(row: RecibidaItem): number {
  return isCreditNote(row.tipoCFE)
    ? -(row.totalIVA ?? 0)
    : (row.totalIVA ?? 0);
}

export { isCreditNote };
